import React, { useEffect, useRef, useState } from 'react';
import {
  useGetInvoiceFullObject,
  useUpdateInvoiceFullObject,
} from '../hooks/reactQuerys/InvoiceQueries';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
} from '@mui/material';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { NoDataDialog } from '../core/NoDataDialog';
import { invoiceFullSchema } from '../Interfaces/IInvoice';
import { InvoiceEdit } from './InvoiceEdit';
import { ILegalTransactionType } from '../Interfaces/ILegalTransactionType';
import { IinvoiceState } from '../Interfaces/IInvoiceState';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { useAlert } from '../context/AlertContext';
import { badRequestText } from '../hooks/useFetch';
import { useGetLegalTransactionFullObject } from '../hooks/reactQuerys/LegalTransactionQueries';

interface IProps {
  idInvoice: number;
  legalTransactionTypes: ILegalTransactionType[];
  invoiceStates: IinvoiceState[];
  legalClients?: ILegalClient[];

  onClose: () => void;
  onSave: () => void;
}

export const getEmptyInvoiceFull = () => {
  return invoiceFullSchema.parse({});
};

export const InvoiceDialog: React.FC<IProps> = ({
  idInvoice,
  legalTransactionTypes,
  invoiceStates,
  legalClients,
  onClose,
  onSave,
}) => {
  const { showAlert } = useAlert();
  const formRef = useRef<HTMLFormElement>(null);
  const {
    invoiceFullObject,
    isFetchingInvoiceFullObject,
    refetchInvoiceFullObject,
  } = useGetInvoiceFullObject(idInvoice);
  const {
    legalTransactionFullObject,
    isFetchingLegalTransactionFullObject,
    refetchLegalTransactionFullObject,
  } = useGetLegalTransactionFullObject(
    legalClients === undefined && invoiceFullObject?.idLegalTransaction
      ? invoiceFullObject?.idLegalTransaction
      : undefined
  );
  //
  const [editInvoice, setEditInvoice] = useState(getEmptyInvoiceFull());
  //
  const { isPendingUpdateInvoiceFullObject, updateInvoiceFullObject } =
    useUpdateInvoiceFullObject();

  const isPending = isPendingUpdateInvoiceFullObject;

  useEffect(() => {
    if (invoiceFullObject) {
      setEditInvoice(invoiceFullObject);
    }
  }, [invoiceFullObject]);

  const handleSubmit = async () => {
    try {
      const preparedObject = {
        ...editInvoice,
        LegalTransactionLegalClients: [],
      };
      const updateObject = invoiceFullSchema.parse(preparedObject);
      await updateInvoiceFullObject(updateObject);
      showAlert({
        severity: 'success',
        text: 'Erfolgreich gespeichert!',
      });
      onSave();
    } catch (error) {
      console.error(error);
      showAlert({
        severity: 'error',
        text: badRequestText(error) ?? 'Es ist ein Fehler aufgetretten!',
      });
    }
  };

  const handleOnSave = () => {
    if (formRef.current && formRef.current.checkValidity()) {
      handleSubmit();
    } else if (formRef.current && !formRef.current.checkValidity()) {
      const elements = Array.from(formRef.current.elements).reverse();

      elements.forEach((element: any) => {
        if (element.reportValidity()) {
          element.reportValidity();
        }
      });
    }
  };

  if (isFetchingInvoiceFullObject) {
    return (
      <Dialog open maxWidth='lg' fullWidth>
        <DialogContent>
          <LoadingSpinner height='50vh' />
        </DialogContent>
      </Dialog>
    );
  }

  if (!invoiceFullObject) {
    return (
      <NoDataDialog
        width='lg'
        unmount={onClose}
        refetch={() => {
          refetchInvoiceFullObject();
        }}
      />
    );
  }

  return (
    <Dialog open maxWidth='lg' fullWidth>
      <form ref={formRef}>
        <DialogContent>
          {isFetchingLegalTransactionFullObject ? (
            <Box sx={{ mb: 3 }}>
              Vorgangsinformationen werden geladen... <LinearProgress />
            </Box>
          ) : (
            <Box sx={{ mt: 6 }} />
          )}

          <InvoiceEdit
            key={`edit-invoce-${editInvoice.idInvoice}`}
            editInvoice={editInvoice}
            setEditInvoice={setEditInvoice}
            invoiceStates={invoiceStates}
            legalClients={
              legalClients
                ? legalClients
                : legalTransactionFullObject?.LegalClientArray || []
            }
            legalTransactionTypes={legalTransactionTypes}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={isPending} variant='outlined' onClick={onClose}>
            Abbruch
          </Button>
          <Button
            variant='contained'
            onClick={handleOnSave}
            disabled={isPending}
          >
            {isPending ? (
              <Box display='flex' alignItems='center' gap={1}>
                <span>Speichern</span>
                <CircularProgress size={20} />
              </Box>
            ) : (
              'Speichern'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
